/**
 * Entitlements Service
 *
 * Single source of truth for determining studio mode and feature access.
 * Separates tier (what plan) from status (is billing current).
 */

import type { Family } from '@/types/database'
import type { StudioMode, StudioLimits, SubscriptionStatus } from '@/types/studio'
import type { UserEntitlementOverride, EffectiveEntitlements } from '@/types/admin'

/**
 * Statuses that grant full cloud access (can read AND write)
 */
const ACTIVE_STATUSES: SubscriptionStatus[] = ['trialing', 'active']

/**
 * Statuses where user can READ cloud data but not write
 */
const READ_ONLY_STATUSES: SubscriptionStatus[] = ['past_due', 'canceled', 'lapsed']

/**
 * Determines the studio mode for a family.
 *
 * Mode logic:
 * - 'cloud' if tier === 'premium' AND status in (trialing, active)
 * - 'local' otherwise
 */
export function getStudioMode(family: Family | null): StudioMode {
  if (!family) return 'local'

  // Free tier always uses local storage
  if (family.subscription_tier !== 'premium') return 'local'

  // Premium tier with active billing uses cloud
  const status = family.subscription_status as SubscriptionStatus | null
  if (status && ACTIVE_STATUSES.includes(status)) {
    return 'cloud'
  }

  // Premium tier without active billing falls back to local for new stories
  // but can still READ cloud (handled separately by canReadCloud)
  return 'local'
}

/**
 * Returns true if the family can READ cloud data.
 * Premium tier (any status) can read their saved work.
 */
export function canReadCloud(family: Family | null): boolean {
  if (!family) return false
  return family.subscription_tier === 'premium'
}

/**
 * Returns true if the user is in read-only mode.
 * This is when they have premium tier but billing has lapsed.
 */
export function isReadOnly(family: Family | null): boolean {
  if (!family) return false
  if (family.subscription_tier !== 'premium') return false

  const status = family.subscription_status as SubscriptionStatus | null
  if (!status) return false

  return READ_ONLY_STATUSES.includes(status)
}

/**
 * Returns true if subscription has lapsed (was active, now isn't)
 */
export function isLapsed(family: Family | null): boolean {
  return isReadOnly(family)
}

/**
 * Returns true if user is in trial period
 */
export function isTrialing(family: Family | null): boolean {
  if (!family) return false
  return family.subscription_status === 'trialing'
}

/**
 * Returns feature limits based on studio mode and read-only state.
 */
export function getLimits(mode: StudioMode, readOnly: boolean = false): StudioLimits {
  if (mode === 'cloud') {
    return {
      maxActiveStories: Infinity,
      canAccessDashboard: true,
      canAccessBadges: true,
      canAccessSkillTracking: true,
      canExportStories: true,
      readOnly,
    }
  }

  // Local mode (free tier)
  return {
    maxActiveStories: 1,
    canAccessDashboard: false,  // Basic version only
    canAccessBadges: false,
    canAccessSkillTracking: false,
    canExportStories: false,
    readOnly: false,  // Local mode is always writable
  }
}

/**
 * Determines which repository should be used.
 * Premium tier always uses cloud repo (even when lapsed) so they can see saved work.
 * Free tier uses local repo.
 */
export function shouldUseCloudRepository(family: Family | null): boolean {
  if (!family) return false
  return family.subscription_tier === 'premium'
}

/**
 * Computes effective entitlements by merging subscription with admin overrides.
 *
 * Precedence ladder:
 * 1. If override.disabled = true → deny everything (even if paid)
 * 2. Check if override is expired → treat as no override
 * 3. Base entitlements from subscription tier/status
 * 4. Apply override if active (premium_enabled or beta_enabled)
 * 5. Cloud mode = premium access (during beta, premium_enabled grants cloud)
 */
export function getEffectiveEntitlements(
  family: Family | null,
  override: UserEntitlementOverride | null
): EffectiveEntitlements {
  // 1. If disabled, deny everything (even if paid)
  if (override?.disabled) {
    return { blocked: true, reason: 'access_paused' }
  }

  // 2. Check if override is expired
  const overrideActive = override && (
    !override.override_expires_at ||
    new Date(override.override_expires_at) > new Date()
  )

  // 3. Base entitlements from subscription
  const status = family?.subscription_status as SubscriptionStatus | null
  const basePremium = family?.subscription_tier === 'premium' &&
    status !== null &&
    ACTIVE_STATUSES.includes(status)

  // 4. Apply override if active
  const hasPremiumAccess = basePremium || (overrideActive && override?.premium_enabled)
  const hasBetaAccess = overrideActive && override?.beta_enabled

  // 5. Cloud mode = premium access (during beta, premium_enabled grants cloud)
  const studioMode: StudioMode = hasPremiumAccess ? 'cloud' : 'local'

  return {
    blocked: false,
    studioMode,
    hasPremiumAccess: !!hasPremiumAccess,
    hasBetaAccess: !!hasBetaAccess,
    source: overrideActive ? 'override' : 'subscription',
  }
}
